import {
	Box,
	Button,
	DialogContent,
	Grid,
	makeStyles,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { useCompany } from "Components/Company/hooks/useCompany";
import { getValidMembers } from "Components/Members/MembersHelper";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import ModalActions from "Components/Modal/ModalActions";
import ModalTitle from "Components/Modal/ModalTitle";
import {
	getProjectAddressString,
	getProjectContactsString,
	useIsProjectRedirectable,
} from "Components/Project/ProjectHelper";
import { useProjects } from "Components/Project/hooks/useProjects";
import { useTranslateScheduleEntryType } from "Components/ScheduleEntryType/ScheduleEntryTypeHelper";
import { useScheduleEntryTypes } from "Components/ScheduleEntryType/hooks/useScheduleEntryTypes";
import { useScheduleResources } from "Components/ScheduleResource/hooks/useScheduleResources";
import { px } from "Configs/theme";
import { useRedirectToProject } from "Helpers/RedirectHelper";
import { useTranslation } from "Providers/TranslationProvider";
import { ScheduleEntry } from "domain-entities";
import moment from "moment";
import React, { FC, Fragment } from "react";

interface Props {
	scheduleEntry: ScheduleEntry;
	close: () => void;
	edit: () => void;
}

const ScheduleEntryDatailsView: FC<Props> = (props) => {
	const { t } = useTranslation();
	const companyMembers = useCompany((state) => state.item?.members);
	const isInternalAdmin = useIsInternalAdmin();
	const translateScheduleEntryType = useTranslateScheduleEntryType();
	const classes = useStyles();
	const resourceName = useScheduleResources((state) => {
		if (!props.scheduleEntry.resourceId) return;
		const scheduleResource = state.items.find(
			(item) => item.id === props.scheduleEntry.resourceId
		);
		return scheduleResource?.name;
	});

	const momentFormat = `dddd, ${t("Common_Format_Date")} ${t(
		"Common_Format_Time"
	)}`;

	const scheduleEntryType = useScheduleEntryTypes((state) =>
		state.items.find((item) => item.id === props.scheduleEntry.typeId)
	);

	const startTime = moment.unix(props.scheduleEntry.startTime);
	const endTime = moment.unix(props.scheduleEntry.endTime);
	const projects = useProjects((state) => state.projects);
	const project = props.scheduleEntry.projectId
		? projects.find((item) => item.id === props.scheduleEntry.projectId)
		: undefined;

	const member = props.scheduleEntry.userId
		? getValidMembers(companyMembers).find(
				(item) => item.id === props.scheduleEntry.userId
		  )
		: undefined;

	const addressString = project ? getProjectAddressString(project) : undefined;
	const contactsString = project
		? getProjectContactsString(project)
		: undefined;

	const redirectToProject = useRedirectToProject(projects);
	const isProjectRedirectable = useIsProjectRedirectable(project);

	return (
		<Fragment>
			<ModalTitle
				close={props.close}
				isCloseAutoFocusEnabled
				title={t("ScheduleEntryDetailsView_Title")}
			>
				{isInternalAdmin && (
					<Button onClick={props.edit} variant="contained" color="primary">
						<EditIcon />
					</Button>
				)}
			</ModalTitle>
			<DialogContent>
				{project && (
					<Grid container>
						<Grid item>
							<Box className={classes.labelBox}>
								{t("ScheduleEntryDetailsView_Label_ProjectName")}
							</Box>
							<Box className={classes.valueBox}>{project.name}</Box>
						</Grid>
					</Grid>
				)}
				{scheduleEntryType && (
					<Grid container>
						<Grid item>
							<Box className={classes.labelBox}>
								{t("ScheduleEntryDetailsView_Label_ScheduleEntryType")}
							</Box>
							<Box className={classes.valueBox}>
								{translateScheduleEntryType(scheduleEntryType)}
							</Box>
						</Grid>
					</Grid>
				)}
				<Grid container>
					<Grid item xs={12} sm={6}>
						<Box className={classes.labelBox}>
							{t("ScheduleEntryDetailsView_Label_StartTime")}
						</Box>
						<Box className={classes.valueBox}>
							<strong>{startTime.format(momentFormat)}</strong>
						</Box>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Box className={classes.labelBox}>
							{t("ScheduleEntryDetailsView_Label_EndTime")}
						</Box>
						<Box className={classes.valueBox}>
							<strong>{endTime.format(momentFormat)}</strong>
						</Box>
					</Grid>
				</Grid>
				{member && (
					<Grid container>
						<Grid item>
							<Box className={classes.labelBox}>
								{t("ScheduleEntryDetailsView_Label_User")}
							</Box>
							<Box className={classes.valueBox}>
								{member.name} {member.lastname}
							</Box>
						</Grid>
					</Grid>
				)}
				{resourceName && (
					<Grid container>
						<Grid item>
							<Box className={classes.labelBox}>
								{t("ScheduleEntryDetailsView_Label_Resource")}
							</Box>
							<Box className={classes.valueBox}>{resourceName}</Box>
						</Grid>
					</Grid>
				)}
				{addressString && (
					<Grid container>
						<Grid item>
							<Box className={classes.labelBox}>
								{t("ScheduleEntryDetailsView_Label_Address")}
							</Box>
							<Box className={classes.valueBox}>{addressString}</Box>
						</Grid>
					</Grid>
				)}
				{contactsString && (
					<Grid container>
						<Grid item>
							<Box className={classes.labelBox}>
								{t("ScheduleEntryDetailsView_Label_Contacts")}
							</Box>
							<Box className={classes.valueBox}>{contactsString}</Box>
						</Grid>
					</Grid>
				)}
				{project?.noteContent && (
					<Grid container>
						<Grid item>
							<Box className={classes.labelBox}>
								{t("ScheduleEntryDetailsView_Label_ProjectNoteContent")}
							</Box>
							<Box
								className={`${classes.valueBox} ${classes.projectNoteContent}`}
							>
								{project.noteContent}
							</Box>
						</Grid>
					</Grid>
				)}
			</DialogContent>
			{isProjectRedirectable && project && (
				<ModalActions>
					<Button
						type="button"
						color="primary"
						startIcon={<OpenInNewIcon />}
						onClick={() => redirectToProject(project.id)}
					>
						{t("Common_Dialog_OpenProject")}
					</Button>
				</ModalActions>
			)}
		</Fragment>
	);
};

export default ScheduleEntryDatailsView;

const useStyles = makeStyles({
	labelBox: {
		marginTop: px.xl,
		display: "flex",
		marginBottom: px.xs,
		fontSize: "12px",
	},
	valueBox: {
		display: "flex",
		fontSize: "16px",
		fontWeight: 500,
	},
	projectNoteContent: {
		whiteSpace: "pre-line",
	},
});
